import type { DetailedMonthlySummaryRow } from '@ferty9/types';
import { ChoicesSelect } from '../components/ChoicesSelect';
import { useMemo, useRef } from 'react';
import type { AdminActions } from '../types';

function DownloadIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 16h5v5" />
    </svg>
  );
}

function statusColor(value: string) {
  if (value === 'Completed') return { background: '#dcfce7', color: '#166534' };
  if (value === 'Pending') return { background: '#fef3c7', color: '#92400e' };
  return { background: '#fee2e2', color: '#991b1b' };
}

function badge(value: string) {
  const color = statusColor(value);
  return <span className="badge" style={{ ...color, fontWeight: 800, borderRadius: 999, padding: '5px 10px', whiteSpace: 'nowrap' }}>{value}</span>;
}

function cell(value: unknown) {
  if (value == null || value === '') return '-';
  return String(value);
}

export function DetailedMonthlySummaryTab({
  active,
  rows,
  centers,
  month,
  search,
  center,
  category,
  appointmentStatus,
  feedbackStatus,
  loaded,
  actions,
}: {
  active: boolean;
  rows: DetailedMonthlySummaryRow[];
  centers: string[];
  month: string;
  search: string;
  center: string;
  category: string;
  appointmentStatus: string;
  feedbackStatus: string;
  loaded: boolean;
  actions: AdminActions;
}) {
  const selectedPeriod = month === 'YTD' || month === 'FY_YTD' ? month : 'MONTH';
  const tableRef = useRef<HTMLTableElement | null>(null);
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch = !term || [
        row.patientId,
        row.patientName,
        row.centerName,
        row.category,
        row.appointmentType,
        row.donorId,
        row.donorName,
      ].some((value) => String(value || '').toLowerCase().includes(term));
      return (
        matchesSearch &&
        (center === 'ALL' || row.centerName === center) &&
        (category === 'ALL' || row.category === category) &&
        (appointmentStatus === 'ALL' || row.appointmentStatus === appointmentStatus) &&
        (feedbackStatus === 'ALL' || row.feedbackStatus === feedbackStatus)
      );
    });
  }, [appointmentStatus, category, center, feedbackStatus, rows, search]);

  return (
    <section id="tab-detailed-summary" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h2 className="section-title" style={{ margin: 0, fontFamily: 'Outfit', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>Detailed Summary</h2>
            <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 13.5, fontWeight: 600 }}>Patient-level appointment and feedback reconciliation for the selected reporting period.</p>
          </div>
          <span className="badge" style={{ background: 'var(--primary-light)', color: 'var(--primary)', fontWeight: 800, borderRadius: 10, padding: '8px 14px' }}>{filteredRows.length} Rows</span>
        </div>

        <div className="filter-row" style={{ alignItems: 'flex-end' }}>
          <div className="input-group"><label>Reporting Period</label><select value={selectedPeriod} onChange={(event) => actions.setDetailedSummaryMonth(event.target.value === 'MONTH' ? currentMonthValue() : event.target.value)}><option value="MONTH">Selected Month</option><option value="YTD">YTD (Year to Date)</option><option value="FY_YTD">FY-YTD (Financial Year to Date)</option></select></div>
          {selectedPeriod === 'MONTH' ? <div className="input-group"><label>Select Month</label><input type="month" value={month} onChange={event => actions.setDetailedSummaryMonth(event.target.value)} /></div> : null}
          <div className="input-group" style={{ minWidth: 240, flex: 1 }}><label>Search Patient / MRN / Donor</label><input value={search} onChange={event => actions.setDetailedSummarySearch(event.target.value)} placeholder="Search by patient, MRN, donor, center..." /></div>
          <div className="input-group" style={{ minWidth: 220 }}><label>Center</label><ChoicesSelect id="detailed-summary-center" value={center} onChange={actions.setDetailedSummaryCenter} options={[{ label: 'All Centers', value: 'ALL' }, ...centers.map(item => ({ label: item, value: item }))]} /></div>
          <div className="input-group" style={{ minWidth: 180 }}><label>Category</label><ChoicesSelect id="detailed-summary-category" value={category} onChange={actions.setDetailedSummaryCategory} searchEnabled={false} options={[{ label: 'All Categories', value: 'ALL' }, { label: 'New Case', value: 'New Case' }, { label: 'OPU', value: 'OPU' }, { label: 'ET', value: 'ET' }]} /></div>
          <div className="input-group" style={{ minWidth: 190 }}><label>Appointment Status</label><ChoicesSelect id="detailed-summary-appointment-status" value={appointmentStatus} onChange={actions.setDetailedSummaryAppointmentStatus} searchEnabled={false} options={[{ label: 'All Statuses', value: 'ALL' }, { label: 'Completed', value: 'Completed' }, { label: 'Not Found', value: 'Not Found' }]} /></div>
          <div className="input-group" style={{ minWidth: 180 }}><label>Feedback Status</label><ChoicesSelect id="detailed-summary-feedback-status" value={feedbackStatus} onChange={actions.setDetailedSummaryFeedbackStatus} searchEnabled={false} options={[{ label: 'All Statuses', value: 'ALL' }, { label: 'Completed', value: 'Completed' }, { label: 'Pending', value: 'Pending' }]} /></div>
          <button className="btn btn-primary report-action-btn" onClick={actions.refreshDetailedSummary}><RefreshIcon />Refresh Data</button>
          <button className="btn btn-outline report-action-btn" onClick={() => void actions.exportDetailedSummary(tableRef.current)}><DownloadIcon />Export Excel</button>
        </div>

        <div style={{ overflowX: 'auto', marginTop: 24 }}>
          <table ref={tableRef} id="detailed-summary-table" className="report-table" style={{ minWidth: 1600, width: '100%' }}>
            <thead>
              <tr>
                <th>Center</th>
                <th>Category</th>
                <th>Patient ID</th>
                <th>Patient Name</th>
                <th>Appointment Date</th>
                <th>Appointment Type</th>
                <th>Appointment Status</th>
                <th>Feedback Status</th>
                <th>NPS Score</th>
                <th>Feedback Date</th>
                <th>Cycle Type</th>
                <th>Donor ID</th>
                <th>Donor Name</th>
                <th>Donor Appointment</th>
                <th>Donor Feedback</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length ? filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{cell(row.centerName)}</td>
                  <td><span className="badge" style={{ background: '#f1f5f9', color: 'var(--text-muted)', fontWeight: 800 }}>{row.category}</span></td>
                  <td style={{ fontWeight: 800 }}>{cell(row.patientId)}</td>
                  <td>{cell(row.patientName)}</td>
                  <td>{cell(row.appointmentDate)}</td>
                  <td>{cell(row.appointmentType)}</td>
                  <td>{badge(row.appointmentStatus)}</td>
                  <td>{badge(row.feedbackStatus)}</td>
                  <td style={{ fontWeight: 800, textAlign: 'center' }}>{row.npsScore ?? '-'}</td>
                  <td>{cell(row.feedbackDate)}</td>
                  <td>{cell(row.cycleType)}</td>
                  <td>{cell(row.donorId)}</td>
                  <td>{cell(row.donorName)}</td>
                  <td>{badge(row.donorAppointmentStatus)}</td>
                  <td>{badge(row.donorFeedbackStatus)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: 44, color: 'var(--text-muted)', fontWeight: 700 }}>
                    {loaded ? 'No detailed rows found for the selected filters.' : 'Please select a month and click "Refresh Data" to load detailed monthly summary.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function currentMonthValue() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
